import React from 'react'
import moment from 'moment'
import {
  Avatar,
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Divider,
} from '@mui/material'

const DetailRow = ({
  label,
  value,
  avatar,
  createdAt,
  fullWidthLabel,
  allowBreakText,
  noBorder,
  children,
  ...props
}) => (
  <Paper
    elevation={0}
    sx={{
      p: noBorder ? 1 : 1.5,
      borderRadius: noBorder ? 1 : 2.5,
      bgcolor: noBorder ? 'inherit' : 'rgba(255,255,255,0.82)',
      border: noBorder ? 'none' : '1px solid rgba(15, 23, 42, 0.15)',

      boxShadow: noBorder
        ? 'none'
        : `
    0 1px 2px rgba(15,23,42,0.03),
    0 6px 18px rgba(15,23,42,0.04)
  `,

      transition: noBorder && 'all 0.2s ease',

      '&:hover': noBorder && {
        transform: 'translateY(-1px)',
        boxShadow: `
      0 2px 6px rgba(15,23,42,0.05),
      0 10px 24px rgba(15,23,42,0.06)
    `,
      },
    }}
    {...props}
  >
    <Stack spacing={0.6}>
      <Stack direction="row" spacing={1} alignItems="center">
        {avatar && <Avatar src={avatar} sx={{ width: 24, height: 24 }} />}

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(15,23,42,0.45)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.68rem',
          }}
        >
          {label}
        </Typography>
      </Stack>

      {createdAt && (
        <Typography variant="caption" color="text.disabled">
          {moment(createdAt).format('DD/MM/YYYY HH:mm')}
        </Typography>
      )}

      {value ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'pre-line',
            wordBreak: allowBreakText ? 'break-word' : 'normal',
            overflowWrap: allowBreakText ? 'anywhere' : 'normal',
          }}
        >
          {value}
        </Typography>
      ) : !children ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.disabled',
          }}
        >
          —
        </Typography>
      ) : null}
      {children}
    </Stack>
  </Paper>
)

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const DetailsBox = ({
  title,
  mainTitle,
  hideTitle,
  caption,
  rows,
  singleColumn,
  textButton,
  buttonRight,
  fullWidthLabels,
  withBorder,
  noBorder,
  // Opt-in: drop rows that have nothing to show rather than filling the card
  // with "—" placeholders. Note 0 and false are real values and are kept.
  hideEmpty,
  emptyText,
  children,
  ...props
}) => {
  const visibleRows = (rows ?? [])
    // Unchanged from before: objects that aren't elements are never rendered.
    .filter((row) => {
      if (!row?.value) return true
      if (React.isValidElement(row.value)) return true
      return typeof row.value !== 'object'
    })
    // Only this second pass is new, and only when a page opts in.
    .filter((row) => !hideEmpty || !isEmptyValue(row?.value))

  return (
    <Paper
      elevation={0}
      {...props}
      sx={{
        p: 2,
        borderRadius: 3,
        border: withBorder
          ? '2px solid rgba(35,154,164,0.75)'
          : noBorder
            ? 'none'
            : '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: noBorder ? 'none' : '0 4px 18px rgba(15, 23, 42, 0.04)',
        bgcolor: 'background.paper',
        ...props.sx,
      }}
    >
      <Stack spacing={2}>
        {!hideTitle && (
          <>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {!mainTitle && (
                    <Box
                      sx={{
                        width: 6,
                        height: 28,
                        borderRadius: 999,
                        // bgcolor: withBorder ? 'anBlue.main' : 'secondary.main',
                        bgcolor: 'contrast.main'
                      }}
                    />
                  )}

                  <Typography
                    variant={mainTitle ? 'title2' : 'h6'}
                    fontWeight={700}
                  >
                    {title}
                  </Typography>

                  {textButton}
                </Stack>

                {buttonRight && <Box>{buttonRight}</Box>}
              </Stack>
              {caption && <Typography variant="caption">{caption}</Typography>}
            </Stack>

            <Divider />
          </>
        )}

        {visibleRows.length > 0 && (
          <Box>
            <Grid container spacing={1.2}>
              {visibleRows.map((row) => (
                <Grid
                  item
                  xs={12}
                  lg={singleColumn || row.fullWidth ? 12 : 6}
                  key={row.key ?? row.label}
                >
                  <DetailRow
                    label={row.label}
                    value={row.value}
                    avatar={row.avatar}
                    createdAt={row.createdAt}
                    fullWidthLabel={row.fullWidthLabel || fullWidthLabels}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {hideEmpty && rows?.length > 0 && visibleRows.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            {emptyText ?? 'Nothing recorded yet.'}
          </Typography>
        )}

        {children}
      </Stack>
    </Paper>
  )
}

export default DetailsBox
export { DetailRow }
